import api from 'src/api/index';
import i18n from 'src/i18n';
import store from 'src/store/store';
import { showErrorToast } from 'src/store/toast';
import { addUploadItem, updateUploadItem, setStorageExceeded, addStorageUsed, fetchStorageUsage } from 'src/store/upload';
import { convertHeicIfNeeded } from 'src/utils/heicConverter';
import { formatBytes } from 'src/tools/formatBytes';

const CHUNK_FILE_SIZE =  5 * 1024 * 1024;
const STORAGE_WARNING_RATIO = 0.95;

const QUOTA_PATTERN = /insufficient storage|storage quota|quota exceeded|storage limit|storage is full|not enough space|хранилищ|квот/i;
const STORAGE_ERROR_PATTERN = /storage error/i;

// Определяет тип ошибки хранилища по телу ответа бэкенда (совпадение по подстроке,
// т.к. отдельного статус-кода для этого случая бэкенд не выдаёт)
const getStorageErrorKind = (error) => {
	const data = error?.response?.data;

	let body = '';
	if (typeof data === 'string') {
		body = data;
	} else if (data) {
		try {
			body = JSON.stringify(data);
		} catch (e) {
			body = '';
		}
	}

	if (error?.response?.status === 507) return 'quota';
	if (QUOTA_PATTERN.test(body)) return 'quota';
	if (STORAGE_ERROR_PATTERN.test(body)) return 'error';

	console.warn('[Uploader] unrecognized upload error:', error?.response?.status, body);

	return null;
};

const getStorageUsage = async () => {
	const { storageLimit, storageUsed } = store.getState().upload;

	if (storageLimit !== null && storageUsed !== null) {
		return { limit: storageLimit, used: storageUsed };
	}

	return store.dispatch(fetchStorageUsage());
};

export const ensureStorageSpace = async (file, { warnWhenNearLimit = false } = {}) => {
	if (!store.getState().auth.authUser) return;

	const size = Number(file?.size) || 0;
	const usage = await getStorageUsage();

	if (!usage) return;

	const free = Math.max(usage.limit - usage.used, 0);

	if (size <= free) {
		if (warnWhenNearLimit && (usage.used + size) / usage.limit >= STORAGE_WARNING_RATIO) {
			store.dispatch(showErrorToast({ description: i18n.t('toasts.storageQuotaWarning') }));
		}
		return;
	}

	if (free === 0) {
		store.dispatch(setStorageExceeded(true));
		store.dispatch(showErrorToast({ description: i18n.t('toasts.storageFull') }));
	} else {
		store.dispatch(showErrorToast({
			description: i18n.t('toasts.storageQuotaExceeded', {
				size: formatBytes(size),
				free: formatBytes(free),
			}),
		}));
	}

	const error = new Error('storage_not_enough');
	error.toastShown = true;
	throw error;
};

class Uploader {
	
	constructor(file) {

		this.file = file;

	}

	async upload() {

		await ensureStorageSpace(this.file, { warnWhenNearLimit: true });

		if (this.file.size < CHUNK_FILE_SIZE) {

			const streamId = this.#uploadFile();

			return streamId;

		} else {

			const streamId = this.#uploadFileByChunk();

			return streamId;
		}

	}

	#createBlobFromFile(file) {

		return new Promise(( resolve, reject ) => {

			var reader = new FileReader();

			reader.onload = function() {
				resolve(new Blob([reader.result], {type: file.type}));
			};

			reader.onerror = function(error) {
				store.dispatch(showErrorToast({
					description: i18n.t('toasts.failedEncodeFile')
				}));
				reject(error);
			};

			reader.readAsArrayBuffer(file);

		});

	}

	#getUniqueFileName (originalName, existingFiles) {
		let counter = 0;
		let uniqueName = originalName;

		// eslint-disable-next-line no-loop-func
		while (existingFiles.some((file) => file.file.name === uniqueName )) {

			counter++;
			const extensionIndex = originalName.lastIndexOf('.');
			if (extensionIndex !== -1) {
				uniqueName =
				originalName.substring(0, extensionIndex) +
				`(${counter})` +
				originalName.substring(extensionIndex);
			} else {

				uniqueName = `${originalName}(${counter})`;
			}
		}

		return uniqueName;
	};
	

	async #uploadFile() {
	
		try {

			let file = this.file;

			const state = store.getState();

			const fileName = this.#getUniqueFileName(file.name, state.upload.list);
			
			if ( file.name !== fileName ) {

				file = new File([file], fileName, { type: file.type });

			}

			const session = await api.createSession({ 
				fileName: file.name, size: file.size
			});

			var uploadItem = {
				file,
				state: 'progress',
				progress: 0
			};

			store.dispatch(addUploadItem(uploadItem));

			// const blob = await this.#createBlobFromFile(file);

			await api.uploadSessionFile( {
				sessionId: session,
				fileChunk: file
			});

			const streamId = await api.closeSession(session);

			store.dispatch(updateUploadItem({...uploadItem, progress: 100}));
			store.dispatch(addStorageUsed(file.size));

			return streamId;
			

		} catch (error) {

			console.error(error);

			// Проверяем ошибку хранилища (S3 или превышение квоты)
			const storageErrorKind = getStorageErrorKind(error);

			store.dispatch(showErrorToast({
				description: storageErrorKind === 'quota'
					? i18n.t('toasts.storageFull')
					: storageErrorKind === 'error'
						? i18n.t('toasts.storageError')
						: i18n.t('toasts.failedCreateSession')
			}));

			store.dispatch(updateUploadItem({...uploadItem, state: 'error'}));

			if (storageErrorKind === 'quota') {
				store.dispatch(setStorageExceeded(true));
			}

			error.toastShown = true;

			throw error;

		}

	}

	async #uploadFileByChunk( chunkSize = CHUNK_FILE_SIZE )  {

		try {

			let file = this.file;

			const state = store.getState();

			const fileName = this.#getUniqueFileName(file.name, state.upload.list);
			
			if ( file.name !== fileName ) {

				file = new File([file], fileName, { type: file.type });

			}

			var uploadItem = {
				file,
				state: 'progress',
				progress: 0
			};

			let start = 0;

			let end = chunkSize;

			const session = await api.createSession({
				fileName: file.name,
				size: file.size
			});

			store.dispatch(addUploadItem( uploadItem ));

			// const blob = await this.#createBlobFromFile(file);

			while ( start < file.size ) {

				const chunk = file.slice(start, end);

				await api.uploadSessionFile( {
					sessionId: session,
					fileChunk: chunk
				});

				start = end;
 
				uploadItem.progress = Math.ceil( ( start / file.size ) * 100 );

				store.dispatch(updateUploadItem({...uploadItem}));

				end = Math.min( start + chunkSize, file.size );

			}

			const streamId = await api.closeSession(session);

			store.dispatch(updateUploadItem({...uploadItem, progress: 100}));
			store.dispatch(addStorageUsed(file.size));

			return streamId;
			
		} catch (error) {

			console.error(error);
			
			// Проверяем ошибку хранилища (S3 или превышение квоты)
			const storageErrorKind = getStorageErrorKind(error);

			store.dispatch(showErrorToast({
				description: storageErrorKind === 'quota'
					? i18n.t('toasts.storageFull')
					: storageErrorKind === 'error'
						? i18n.t('toasts.storageError')
						: i18n.t('toasts.failedUploadFile')
			}));

			store.dispatch(updateUploadItem({...uploadItem, state: 'error'}));

			if (storageErrorKind === 'quota') {
				store.dispatch(setStorageExceeded(true));
			}

			error.toastShown = true;

			throw error;

		}

	}

	static async uploadRegisterImage (file) {

		try {

			file = await convertHeicIfNeeded(file);

			const fileId = await api.uploadRegisterImage(file);

			return fileId;

		} catch (error) {

			console.error(error);

			const storageErrorKind = getStorageErrorKind(error);

			store.dispatch(showErrorToast({
				description: storageErrorKind === 'quota'
					? i18n.t('toasts.storageFull')
					: storageErrorKind === 'error'
						? i18n.t('toasts.storageError')
						: i18n.t('toasts.failedUploadImage')
			}));

			if (storageErrorKind === 'quota') {
				store.dispatch(setStorageExceeded(true));
			}

			error.toastShown = true;

		}

	};

	static async uploadImage (file) {

		try {

			await ensureStorageSpace(file);

			file = await convertHeicIfNeeded(file);
			console.log('[uploadImage] file:', file?.name, file?.type, file?.size);

			const fileId = await api.uploadImage(file);

			store.dispatch(addStorageUsed(file.size));

			return fileId;

		} catch (error) {

			console.error(error);

			const storageErrorKind = getStorageErrorKind(error);

			if (!error.toastShown) {
				store.dispatch(showErrorToast({
					description: storageErrorKind === 'quota'
						? i18n.t('toasts.storageFull')
						: storageErrorKind === 'error'
							? i18n.t('toasts.storageError')
							: i18n.t('toasts.failedUploadImage')
				}));
			}

			if (storageErrorKind === 'quota') {
				store.dispatch(setStorageExceeded(true));
			}

			error.toastShown = true;

			throw error;

		}

	}

	async uploadAttachmentOnModule(id, moduleId, type = 'course') {

		try {

			await ensureStorageSpace(this.file);

			// const blob = await this.#createBlobFromFile(this.file);
			
			const fileId = await api.uploadAttachmentModule({ id, moduleId, file: this.file, fileName: this.file.name, type });

			store.dispatch(addStorageUsed(this.file.size));

			return fileId;

		} catch (error) {

			console.error(error);

			const storageErrorKind = getStorageErrorKind(error);

			if (!error.toastShown) {
				store.dispatch(showErrorToast({
					description: storageErrorKind === 'quota'
						? i18n.t('toasts.storageFull')
						: storageErrorKind === 'error'
							? i18n.t('toasts.storageError')
							: i18n.t('toasts.failedUploadAttachment')
				}));
			}

			if (storageErrorKind === 'quota') {
				store.dispatch(setStorageExceeded(true));
			}

			error.toastShown = true;

			throw error;

		}

	}

}

export default Uploader;